import { useContext, useEffect, useCallback } from 'react'
import getPublishers from '../repository/getPublishers'
import { currentRole } from '../../../authProvider'
import { SalesContext } from '../storage/SalesProvider'
import affiliateLinkCopied from '../../../lib/segment/events/affiliateLinkCopied'
import getPartner from '../../Account/repository/getPartner'
import Partner from '../types/Partner'

const useSales = () => {
  const context = useContext(SalesContext)

  if (!context) {
    throw new Error('useSales must be used within a SalesProvider')
  }

  const role = currentRole()
  const { data, setData } = context
  const currentRoleId = role?.id
  const loading = currentRoleId !== data.roleId

  const fetchData = useCallback(async () => {
    if (loading) {
      const publishers = await getPublishers()
      const result = await getPartner()

      if ('error' in result) {
        setData({
          publishers,
          error: result.error,
          roleId: currentRoleId,
          publisherId: publishers[0]?.id
        })
        return
      }

      setData({
        publishers,
        partner: result.partner as Partner,
        roleId: currentRoleId,
        publisherId: publishers[0]?.id
      })
    }
  }, [setData, currentRoleId, loading])

  const getSaleLink = (sale: { slug: string }) => {
    const role = currentRole()

    if (!role) return

    const { publishers, partner, publisherId } = data
    const { type: roleType, subname } = role
    const publisher = publishers.find(({ id }) => id === publisherId)

    if (roleType === 'AFFILIATE' && partner?.type === 'Affiliate') {
      return `https://${publisher?.hostname}/sales/${sale.slug}?aid=${partner?.code}`
    }

    if (roleType === 'PUBLISHER' && partner?.type === 'Publisher') {
      return `https://${subname}/sales/${sale.slug}`
    }

    if (roleType === 'VENDOR' && partner?.type === 'Vendor') {
      return `https://www.stacksocial.com/sales/${sale.slug}`
    }
  }

  const sendLinkCopiedEvent = (saleId: number) => {
    const role = currentRole()

    if (!role) return

    const { id: affiliateId, type: roleType } = role
    const publisherId = data.publisherId as number

    switch (roleType) {
      case 'AFFILIATE':
        affiliateLinkCopied({ affiliateId, saleId, publisherId })
        break
      default:
        break
    }
  }

  const setCurrentPublisher = (id: number) => {
    setData({ ...data, publisherId: id })
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    loading,
    error: data.error,
    data,
    setCurrentPublisher,
    getSaleLink,
    sendLinkCopiedEvent
  }
}

export default useSales
